import { ArrowUpRight } from "lucide-react";

// Indian creator at premium condenser microphone — YouTube / podcast culture (Unsplash, HowWHYGqFF4)
const CINEMA_IMG =
    "https://images.unsplash.com/photo-1561446289-4112a4f79116?fm=jpg&q=70&w=1200&auto=format&fit=crop";
// Premium multi-mic Indian studio setup — content production (Unsplash, g0PcDhany4Y)
const PERF_IMG =
    "https://images.unsplash.com/photo-1615458317538-72c46d9359d8?fm=jpg&q=70&w=1200&auto=format&fit=crop";

export default function Services() {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative py-28 md:py-36"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <div className="grid md:grid-cols-12 gap-8 mb-20 reveal">
                    <div className="md:col-span-12">
                        <div className="text-[11px] uppercase tracking-[0.3em] text-[#e6192b] mb-5">
                            ⎯⎯ §03 · Services
                        </div>
                        <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-5xl">
                            Two engines.{" "}
                            <span className="italic text-[#a0a0ab]">
                                One synergetic loop
                            </span>{" "}
                            of authority and demand.
                        </h2>
                    </div>
                </div>

                {/* Service 1 */}
                <article
                    data-testid="service-cinematic"
                    className="grid md:grid-cols-12 gap-10 items-stretch mb-24 reveal"
                >
                    <div className="md:col-span-7 relative group overflow-hidden border border-[#26262a]">
                        <img
                            src={CINEMA_IMG}
                            alt="Cinematic camera production"
                            loading="lazy"
                            decoding="async"
                            className="w-full h-[420px] md:h-[560px] object-cover grayscale-[40%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[1200ms] ease-out"
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-[#050505] via-transparent to-transparent" />
                        <div className="absolute top-6 left-6 text-[11px] uppercase tracking-[0.25em] text-white">
                            <span className="text-[#e6192b]">●</span> rec ·
                            01.cinematic
                        </div>
                        <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-white/80">
                            <span>4K · 24fps · ARRI</span>
                            <span>Bengaluru / Mumbai / Remote</span>
                        </div>
                    </div>
                    <div className="md:col-span-5 flex flex-col justify-between">
                        <div>
                            <span className="font-serif-display italic text-7xl text-[#e6192b]/80">
                                01
                            </span>
                            <h3 className="mt-4 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
                                360° YouTube &amp; Cinematic Production.
                            </h3>
                            <p className="mt-6 text-[#a0a0ab] leading-relaxed">
                                Algorithm-driven channel strategy fused with
                                documentary-grade craft. We don&apos;t produce
                                content — we engineer reputational artefacts
                                that compound viewership and authority.
                            </p>
                            <ul className="mt-8 space-y-3 text-[#cfcfd6]">
                                {[
                                    "Full-scale YouTube management & data-led growth",
                                    "Premium brand films & founder narratives",
                                    "Channel-as-revenue-engine modelling",
                                    "Lasting Reputation Capital, on tape",
                                ].map((t) => (
                                    <li
                                        key={t}
                                        className="flex items-start gap-3 text-[15px]"
                                    >
                                        <span className="mt-2 h-px w-5 bg-[#e6192b] flex-shrink-0" />
                                        {t}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </article>

                {/* Service 2 — flipped */}
                <article
                    data-testid="service-performance"
                    className="grid md:grid-cols-12 gap-10 items-stretch reveal"
                >
                    <div className="md:col-span-5 md:order-1 order-2 flex flex-col justify-between">
                        <div>
                            <span className="font-serif-display italic text-7xl text-[#e6192b]/80">
                                02
                            </span>
                            <h3 className="mt-4 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
                                Performance Advertising &amp; ROI.
                            </h3>
                            <p className="mt-6 text-[#a0a0ab] leading-relaxed">
                                A media-buying desk built for high-ticket B2B,
                                not e-commerce thrash. Every rupee deployed is
                                attributed, audited, and tied to qualified
                                pipeline — not pixel-fired vanity.
                            </p>
                            <ul className="mt-8 space-y-3 text-[#cfcfd6]">
                                {[
                                    "Strategic buys across Meta & Google ecosystems",
                                    "Optimised for complex sales cycles & SQLs",
                                    "ROI-first metrics, not impressions theatre",
                                    "Quarterly board-ready performance reads",
                                ].map((t) => (
                                    <li
                                        key={t}
                                        className="flex items-start gap-3 text-[15px]"
                                    >
                                        <span className="mt-2 h-px w-5 bg-[#e6192b] flex-shrink-0" />
                                        {t}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="md:col-span-7 md:order-2 order-1 relative group overflow-hidden border border-[#26262a]">
                        <img
                            src={PERF_IMG}
                            alt="Studio microphone — the podium"
                            loading="lazy"
                            decoding="async"
                            className="w-full h-[420px] md:h-[560px] object-cover grayscale-[40%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[1200ms] ease-out"
                        />
                        <div className="absolute inset-0 bg-gradient-to-tl from-[#050505] via-transparent to-transparent" />
                        <div className="absolute top-6 right-6 text-[11px] uppercase tracking-[0.25em] text-white">
                            02.performance ·{" "}
                            <span className="text-[#e6192b]">●</span> live
                        </div>
                        <div className="absolute bottom-6 right-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/80">
                            CPL ↓ · LTV ↑
                            <ArrowUpRight size={12} />
                        </div>
                    </div>
                </article>
            </div>
        </section>
    );
}
